import React from 'react';
import { 
  BarChart3, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Activity,
  Target 
} from 'lucide-react';

const FunnelStatsCards = ({ summary, loading }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  const formatPercentage = (rate) => {
    return `${(rate || 0)?.toFixed(1)}%`;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US')?.format(num || 0);
  };

  if (loading) {
    return (
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)]?.map((_, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="animate-pulse">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 bg-gray-200 rounded"></div>
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                </div>
                <div className="w-16 h-8 bg-gray-200 rounded mb-1"></div>
                <div className="w-20 h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="px-6 py-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
          <div className="text-gray-500">No funnel data available</div>
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Funnels',
      value: formatNumber(summary?.total_funnels || 0),
      icon: BarChart3,
      color: 'blue',
      trend: null
    },
    {
      title: 'Active Leads',
      value: formatNumber(summary?.total_leads || 0),
      icon: Users,
      color: 'green',
      trend: null
    },
    {
      title: 'Conversions',
      value: formatNumber(summary?.total_conversions || 0),
      icon: Target,
      color: 'purple',
      trend: null
    },
    {
      title: 'Conversion Rate',
      value: formatPercentage(summary?.avg_conversion_rate || 0),
      icon: TrendingUp,
      color: 'orange',
      trend: summary?.avg_conversion_rate > 15 ? 'up' : summary?.avg_conversion_rate < 10 ? 'down' : null
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(summary?.total_revenue || 0),
      icon: DollarSign,
      color: 'emerald',
      trend: null
    },
    {
      title: 'Active Funnels',
      value: formatNumber(summary?.funnels?.filter(f => f?.status === 'active')?.length || 0),
      icon: Activity,
      color: 'indigo',
      trend: null
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600',
      emerald: 'bg-emerald-50 text-emerald-600',
      indigo: 'bg-indigo-50 text-indigo-600'
    };
    return colorMap?.[color] || 'bg-gray-50 text-gray-600';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') {
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    }
    if (trend === 'down') {
      return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />;
    }
    return null;
  };

  return (
    <div className="px-6 py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {stats?.map((stat, index) => {
          const IconComponent = stat?.icon;
          
          return (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 rounded-lg ${getColorClasses(stat?.color)}`}>
                  <IconComponent className="w-5 h-5" />
                </div>
                {stat?.trend && getTrendIcon(stat?.trend)}
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {stat?.value}
              </div>
              <div className="text-sm text-gray-600">
                {stat?.title}
              </div>
            </div>
          );
        })}
      </div>
      {/* Quick Performance Overview */}
      {summary?.funnels?.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Funnels</h3>
          <div className="space-y-3">
            {summary?.funnels
              ?.sort((a, b) => (parseFloat(b?.conversion_rate) || 0) - (parseFloat(a?.conversion_rate) || 0))
              ?.slice(0, 3)
              ?.map((funnel, index) => (
                <div key={funnel?.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-800' :
                      index === 1 ? 'bg-gray-100 text-gray-800': 'bg-orange-100 text-orange-800'
                    }`}>
                      #{index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{funnel?.name || 'Unnamed Funnel'}</div>
                      <div className="text-sm text-gray-600">
                        {formatNumber(funnel?.total_leads || 0)} leads • {formatCurrency(funnel?.total_revenue || 0)} revenue
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {formatPercentage(funnel?.conversion_rate || 0)}
                    </div>
                    <div className="text-sm text-gray-600">
                      conversion rate
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FunnelStatsCards;